// Version tracking utility for PWA updates
const VERSION_KEY = 'luvall-app-version';
const LAST_CHECK_KEY = 'luvall-last-update-check';
const UPDATE_CHECK_INTERVAL = 3 * 60 * 1000; // 3 minutes - more aggressive updates

export interface VersionInfo {
  version: string;
  timestamp: number;
  checksum?: string;
}

export const getStoredVersion = (): VersionInfo | null => {
  try {
    const stored = localStorage.getItem(VERSION_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
};

export const setStoredVersion = (version: VersionInfo): void => {
  try {
    localStorage.setItem(VERSION_KEY, JSON.stringify(version));
  } catch {
    // Ignore localStorage errors
  }
};

export const shouldCheckForUpdate = (): boolean => {
  try {
    const lastCheck = localStorage.getItem(LAST_CHECK_KEY);
    if (!lastCheck) return true;
    
    const lastCheckTime = parseInt(lastCheck, 10);
    const { isAndroid } = detectMobileBrowser();
    
    // More frequent checks for mobile (2 minutes for Android, 3 minutes for others)
    const interval = isAndroid ? 2 * 60 * 1000 : UPDATE_CHECK_INTERVAL;
    return Date.now() - lastCheckTime > interval;
  } catch {
    return true;
  }
};

export const markUpdateCheckTime = (): void => {
  try {
    localStorage.setItem(LAST_CHECK_KEY, Date.now().toString());
  } catch {
    // Ignore localStorage errors
  }
};

export const getCurrentVersion = async (): Promise<string> => {
  try {
    // Get version from build meta tag or use a static version
    const metaVersion = document.querySelector('meta[name="version"]')?.getAttribute('content');
    if (metaVersion) {
      return metaVersion;
    }
    
    // Use a static version string for consistent comparison
    return 'v1.0.0';
  } catch {
    return 'v1.0.0';
  }
};

export const detectMobileBrowser = (): { isIOS: boolean; isAndroid: boolean; isMobile: boolean } => {
  try {
    // Safe check for navigator - prevents crashes in edge cases
    if (typeof navigator === 'undefined' || !navigator.userAgent) {
      return { isIOS: false, isAndroid: false, isMobile: false };
    }
    
    const userAgent = navigator.userAgent.toLowerCase();
    const isIOS = /ipad|iphone|ipod/.test(userAgent);
    const isAndroid = /android/.test(userAgent);
    const isMobile = isIOS || isAndroid || /mobile/.test(userAgent);
    
    return { isIOS, isAndroid, isMobile };
  } catch {
    // Fallback for any errors
    return { isIOS: false, isAndroid: false, isMobile: false };
  }
};